'use client';

/* eslint-disable @next/next/no-img-element -- Transparent cutouts are framed by their bounds; next/image would crop or resample them. */

import { useState } from 'react';
import { ThemedImage } from '@/components/ui/themed-image';
import { productThumbnail, useStorefrontArt } from '@/lib/api/storefront-media';
import { cn } from '@/lib/utils';

interface ProductThumbImageProps {
  product: { id: number; name: string; image_url?: string | null };
  sizes?: string;
  className?: string;
}

// Фото товара в корзине и оформлении. Вырезка нового дизайна кадрируется по
// своим границам (bounds) и вписывается целиком с полями — так блюдо не тонет в
// прозрачных краях холста. Обычное фото из приложения заполняет рамку (cover), как раньше.
export function ProductThumbImage({ product, sizes, className }: ProductThumbImageProps) {
  const thumb = productThumbnail(product, useStorefrontArt());
  const [failed, setFailed] = useState<string | null>(null);

  if (thumb.studio && failed !== thumb.src) {
    const { bounds, width: canvasWidth } = thumb.studio;
    const [x, y, right, bottom] = bounds;
    const width = right - x;
    const height = bottom - y;
    const landscape = width >= height;
    return (
      <span className={cn('absolute inset-[8%] flex items-center justify-center', className)}>
        <span
          className="relative block"
          style={{
            aspectRatio: `${width} / ${height}`,
            width: landscape ? '100%' : 'auto',
            height: landscape ? 'auto' : '100%',
          }}
        >
          <img
            src={thumb.src}
            alt={product.name}
            draggable={false}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(thumb.src)}
            className="absolute max-w-none h-auto"
            style={{
              width: `${(canvasWidth / width) * 100}%`,
              left: `${(-x / width) * 100}%`,
              top: `${(-y / height) * 100}%`,
            }}
          />
        </span>
      </span>
    );
  }

  // Вырезка не загрузилась — показываем фото из приложения
  const src = thumb.studio ? product.image_url || '/placeholder.png' : thumb.src;
  return (
    <ThemedImage
      src={src}
      alt={product.name}
      fill
      sizes={sizes}
      className={cn('object-cover', className)}
    />
  );
}
